import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackMetaPixelInitiateCheckout, trackMetaPixelPurchase, trackMetaPixelStartTrial, trackMetaPixelSubscribe } from '../utils/metaPixel';
import { toast } from 'sonner';
import { Check, Users, User, ChevronDown, CreditCard, Shield, Clock, School, Phone, Mail, MapPin, Briefcase } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SchoolEnquiryDialog } from '@/components/SchoolEnquiryDialog';
import { PricingOffer } from '@/components/PricingOffer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const DURATION_ORDER = ['1_day', '1_month', '6_months', '1_year'];
const DURATION_LABELS = {
  '1_day': { short: '1 Day', tag: 'Try it out' },
  '1_month': { short: '1 Month', tag: 'Most Popular' },
  '6_months': { short: '6 Months', tag: 'Save 17%' },
  '1_year': { short: '1 Year', tag: 'Best Value' },
};

export default function PricingSection() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [planMeta, setPlanMeta] = useState(null);
  const [selectedPlanType, setSelectedPlanType] = useState('single_parent');
  const [selectedDuration, setSelectedDuration] = useState('6_months');
  const [numChildren, setNumChildren] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ name: '', email: '', phone: '', referral_code: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSchoolEnquiry, setShowSchoolEnquiry] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/plans`);
        setPlans(res.data.plans);
        setPlanMeta(res.data.plan_types);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      }
    };
    fetchPlans();
  }, []);

  // Keep a ref so the coinquest:buy-now event listener (which closes over a
  // stale plans value) can still read the current plans.
  const plansRef = useRef(null);
  useEffect(() => { plansRef.current = plans; }, [plans]);

  // Fire Meta Pixel InitiateCheckout when the checkout dialog opens.
  // Guards:
  //   - Public pages only (/  and /register). PricingSection is also shown
  //     inside logged-in contexts (AuthPage, TrialBanner, TrialLimitDialog,
  //     SubscriptionExpiredGate) — those must not fire pixel events.
  //   - At most once per browser session per plan+duration combo so that
  //     repeated opens of the same dialog don't send duplicate events.
  const fireInitiateCheckout = (duration, planType, price) => {
    const path = window.location.pathname;
    if (path !== '/' && path !== '/register') return;
    const key = `cq_ic_${planType}_${duration}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    const contentName = DURATION_LABELS[duration]?.short || duration;
    trackMetaPixelInitiateCheckout(price, contentName);
  };

  // Allow other components (e.g. the LandingPage hero "Start for ₹49" CTA) to
  // deep-link straight into the 1-day Buy Now flow. Dispatch:
  //   window.dispatchEvent(new CustomEvent('coinquest:buy-now', { detail: { duration: '1_day' } }))
  useEffect(() => {
    const openBuyNow = (evt) => {
      const duration = evt?.detail?.duration || '1_day';
      const planType = evt?.detail?.plan_type || 'single_parent';
      setSelectedPlanType(planType);
      setSelectedDuration(duration);
      setNumChildren(1);
      // Scroll pricing into view so the user has context for the checkout dialog
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      // Small delay so the scroll settles before the modal opens
      setTimeout(() => {
        const currentPlans = plansRef.current;
        if (currentPlans) {
          const plan = currentPlans[planType]?.[duration];
          if (plan) fireInitiateCheckout(duration, planType, plan.base_price);
        }
        setShowCheckout(true);
      }, 400);
    };
    window.addEventListener('coinquest:buy-now', openBuyNow);
    return () => window.removeEventListener('coinquest:buy-now', openBuyNow);
  }, []);

  if (!plans || !planMeta) return null;

  const currentPlan = plans[selectedPlanType]?.[selectedDuration];
  
  const calcTotal = (plan, children) => {
    if (!plan) return 0;
    let total = plan.base_price;
    const cp = plan.child_prices || [];
    for (let i = 1; i < children; i++) {
      total += (cp[i - 1] || 0);
    }
    return total;
  };
  
  const totalPrice = calcTotal(currentPlan, numChildren);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuyNow = () => {
    setShowCheckout(true);
  };

  const captureLeadQuietly = (form, leadStatus) => {
    if (form.email?.trim()) {
      axios.post(`${API}/subscriptions/capture-lead`, {
        name: form.name, email: form.email, phone: form.phone,
        plan_type: selectedPlanType, duration: selectedDuration, num_children: numChildren,
        lead_status: leadStatus,
      }).catch(() => {});
    }
  };

  const handleCheckoutClose = (open) => {
    if (!open && checkoutForm.email?.trim()) {
      captureLeadQuietly(checkoutForm, 'form_closed');
    }
    if (!open) setShowReferralInput(false);
    setShowCheckout(open);
  };

  const handlePayment = async () => {
    if (!checkoutForm.name.trim() || !checkoutForm.email.trim() || !checkoutForm.phone.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!checkoutForm.email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (checkoutForm.phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setIsProcessing(true);
    captureLeadQuietly(checkoutForm, 'form_submitted');
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Create order
      const orderRes = await axios.post(`${API}/subscriptions/create-order`, {
        plan_type: selectedPlanType,
        duration: selectedDuration,
        num_children: numChildren,
        subscriber_name: checkoutForm.name,
        subscriber_email: checkoutForm.email,
        subscriber_phone: checkoutForm.phone,
        referral_code: checkoutForm.referral_code.trim() || undefined,
      });

      const { order_id, amount, currency, key_id } = orderRes.data;

      if (!order_id || !key_id) {
        toast.error('Payment gateway configuration error. Please contact support.');
        setIsProcessing(false);
        return;
      }

      const options = {
        key: key_id || RAZORPAY_KEY,
        amount,
        currency,
        name: 'CoinQuest',
        description: `${planMeta[selectedPlanType].label} - ${DURATION_LABELS[selectedDuration].short}`,
        order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/subscriptions/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // --- Meta Pixel purchase events ---
            // amount is in paise; convert to rupees for the pixel value.
            // Guard with razorpay_payment_id so a double-invocation of this
            // handler (however unlikely) never double-fires the events.
            const _payId = response.razorpay_payment_id;
            const _fireKey = `cq_px_${_payId}`;
            if (!sessionStorage.getItem(_fireKey)) {
              sessionStorage.setItem(_fireKey, '1');
              const _valueINR = amount / 100;
              const _contentName = DURATION_LABELS[selectedDuration]?.short || selectedDuration;
              trackMetaPixelPurchase(_valueINR, _contentName, numChildren, _payId);
              if (selectedDuration === '1_day') {
                trackMetaPixelStartTrial(_valueINR, `${_payId}_trial`);
              } else {
                trackMetaPixelSubscribe(_valueINR, _contentName, `${_payId}_sub`);
              }
            }
            // --- end Meta Pixel ---
            toast.success('Payment successful! Setting up your account…');
            setShowCheckout(false);
            setCheckoutForm({ name: '', email: '', phone: '', referral_code: '' });
            // Send the user to the auto-sign-in screen — it will fetch the
            // post-payment context and route them to signup, attach-password,
            // or login-existing depending on their account state.
            navigate(`/complete-signup?order_id=${response.razorpay_order_id}`);
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: checkoutForm.name,
          email: checkoutForm.email,
          contact: checkoutForm.phone,
        },
        theme: { color: '#1D3557' },
        modal: {
          ondismiss: () => setIsProcessing(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setIsProcessing(false);
      });
      setShowCheckout(false);
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Failed to initiate payment: ${detail}`);
      setIsProcessing(false);
    }
  };

  return (
    <section id="pricing" className="py-16 bg-white" data-testid="pricing-section">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: 'Fredoka' }}>
            Start with one day. Stay if it clicks.
          </h2>
          <p className="text-base text-[#3D5A80] max-w-xl mx-auto">
            Every plan includes every story, game and quest, and the parent dashboard.
          </p>
        </div>

        {/* Plan Type Toggle */}
        <div className="flex justify-center mb-5">
          <div className="inline-flex bg-[#E0FBFC] rounded-full p-1 border-2 border-[#1D3557]">
            <button
              data-testid="plan-type-single"
              onClick={() => setSelectedPlanType('single_parent')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all ${
                selectedPlanType === 'single_parent'
                  ? 'bg-[#1D3557] text-white shadow-lg'
                  : 'text-[#1D3557] hover:bg-white/50'
              }`}
            >
              <User className="w-4 h-4" />
              1 parent account
            </button>
            <button
              data-testid="plan-type-two"
              onClick={() => setSelectedPlanType('two_parents')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all ${
                selectedPlanType === 'two_parents'
                  ? 'bg-[#1D3557] text-white shadow-lg'
                  : 'text-[#1D3557] hover:bg-white/50'
              }`}
            >
              <Users className="w-4 h-4" />
              2 parent accounts
            </button>
          </div>
        </div>
        <p className="text-center text-xs text-gray-400 -mt-3 mb-5">
          With 2 accounts, both parents can log in and follow progress.
        </p>
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-3 bg-[#FFF3E0] rounded-full px-5 py-2 border border-[#EE6C4D]/30">
            <span className="text-sm font-bold text-[#1D3557]">Children:</span>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`children-count-${n}`}
                  onClick={() => setNumChildren(n)}
                  className={`w-11 h-11 rounded-full font-bold text-sm transition-all ${
                    numChildren === n
                      ? 'bg-[#EE6C4D] text-white shadow-md scale-110'
                      : 'bg-white text-[#1D3557] hover:bg-[#EE6C4D]/20 border border-[#1D3557]/20'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Duration Cards with Tiered Pricing */}
        <div className="grid grid-cols-1 min-[600px]:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {DURATION_ORDER.map((dur) => {
            const plan = plans[selectedPlanType]?.[dur];
            if (!plan) return null;
            const price = calcTotal(plan, numChildren);
            const isPopular = dur === '6_months';
            const isSelected = selectedDuration === dur;

            // Sub-label helpers
            const monthTotal = plans[selectedPlanType]?.['1_month']
              ? calcTotal(plans[selectedPlanType]['1_month'], numChildren)
              : 0;
            const childLabel = numChildren === 1 ? '1 child' : `${numChildren} children`;
            const fmt = (n) => n.toLocaleString('en-IN');
            let subLabel;
            if (dur === '1_day') {
              subLabel = `₹${fmt(price)} for one day · ${childLabel}`;
            } else if (dur === '1_month') {
              subLabel = `₹${fmt(Math.round(price / 30))} a day · ${childLabel}`;
            } else if (dur === '6_months') {
              const saving = monthTotal * 6 - price;
              subLabel = `₹${fmt(Math.round(price / 180))} a day · you save ₹${fmt(saving)} vs monthly`;
            } else {
              const saving = monthTotal * 12 - price;
              subLabel = `₹${fmt(Math.round(price / 365))} a day · you save ₹${fmt(saving)} vs monthly`;
            }
            const durLabel = {
              '1_day': { name: '1 Day', tag: 'Start here', tagColor: 'bg-[#E0FBFC] text-[#3D5A80]' },
              '1_month': { name: '1 Month', tag: '', tagColor: '' },
              '6_months': { name: '6 Months', tag: 'Best value', tagColor: 'bg-[#E0FBFC] text-[#3D5A80]' },
              '1_year': { name: '1 Year', tag: 'Lowest price per day', tagColor: 'bg-[#D1FAE5] text-[#166534]' },
            }[dur];

            return (
              <div key={dur} className="flex flex-col">
                <div
                  data-testid={`plan-card-${dur}`}
                  onClick={() => setSelectedDuration(dur)}
                  className={`relative cursor-pointer rounded-2xl overflow-hidden flex flex-col border-2 transition-all duration-200 bg-white ${
                    isPopular && isSelected
                      ? 'border-[#1D3557] shadow-[3px_3px_0px_0px_#1D3557]'
                      : isSelected
                      ? 'border-[#06D6A0] shadow-[3px_3px_0px_0px_#06D6A0]'
                      : 'border-[#1D3557]/15 hover:border-[#1D3557]/40 shadow-sm hover:shadow-md'
                  }`}
                >
                  <div className="px-5 pt-5 pb-0">
                    {/* Title row — name never wraps; tag drops to its own line if needed */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
                      <h3 className="text-xl font-bold text-[#1D3557] whitespace-nowrap" style={{ fontFamily: 'Fredoka' }}>{durLabel.name}</h3>
                      {durLabel.tag && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${durLabel.tagColor}`}>{durLabel.tag}</span>
                      )}
                    </div>

                    {/* Base price */}
                    <PricingOffer
                      price={price}
                      discountPercent={plan.discount_percent}
                      discountEndDate={plan.discount_end_date}
                      futurePrice={plan.future_price}
                      priceClassName="text-4xl font-bold text-[#1D3557] mb-0.5"
                      testId={`plan-${dur}`}
                    />
                    <p className="text-xs text-gray-500 mb-3 mt-0.5" data-testid={`plan-sublabel-${dur}`}>
                      {subLabel}
                    </p>
                  </div>

                  {/* Button area */}
                  <div className="mt-auto px-5 pb-5 pt-2">
                    <button
                      data-testid={`buy-now-${dur}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedDuration(dur); fireInitiateCheckout(dur, selectedPlanType, calcTotal(plan, numChildren)); handleBuyNow(); }}
                      className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                        isPopular
                          ? 'bg-[#FFD23F] text-[#1D3557] hover:bg-[#FFE066]'
                          : 'bg-[#06D6A0] text-white hover:bg-[#05C090]'
                      }`}
                    >
                      {{ '1_day': 'Start 1 day', '1_month': 'Choose 1 month', '6_months': 'Choose 6 months', '1_year': 'Choose 1 year' }[dur] || 'Buy Now'}
                    </button>
                  </div>

                  {/* Selected checkmark */}
                  {isSelected && (
                    <div className="absolute top-2.5 right-2.5">
                      <div className="w-6 h-6 bg-[#06D6A0] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-1 mt-5 text-xs text-[#3D5A80]">
          <p className="text-center text-gray-500">Every plan is a one-time payment. Nothing renews automatically.</p>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Secure UPI and card payment</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Instant access</span>
          </span>
        </div>

        {/* School Enquiry CTA */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#1D3557] to-[#3D5A80] rounded-2xl px-8 py-4 shadow-lg">
            <School className="w-6 h-6 text-[#FFD23F]" />
            <div className="text-left">
              <p className="text-white font-bold text-sm" style={{ fontFamily: 'Fredoka' }}>Looking for a School Plan?</p>
              <p className="text-[#E0FBFC] text-xs">We offer special pricing for schools & institutions</p>
            </div>
            <button
              data-testid="school-enquiry-btn"
              onClick={() => setShowSchoolEnquiry(true)}
              className="ml-2 bg-[#FFD23F] text-[#1D3557] font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-[#FFE066] transition-colors shadow-md"
            >
              Enquire Now
            </button>
          </div>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={handleCheckoutClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fredoka' }}>
              Complete Your Purchase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#E0FBFC] rounded-xl p-4 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-[#3D5A80]">{planMeta[selectedPlanType].label}</span>
                <span className="font-bold text-[#1D3557]">{currentPlan?.duration_label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#3D5A80]">{numChildren} Child{numChildren > 1 ? 'ren' : ''}</span>
                <span className="font-bold text-[#1D3557]">₹{totalPrice.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-[#1D3557] mb-1 block">Full Name</label>
              <Input
                data-testid="checkout-name"
                placeholder="Enter your full name"
                autoComplete="name"
                value={checkoutForm.name}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-[#1D3557] mb-1 block">Email</label>
              <Input
                data-testid="checkout-email"
                type="email"
                placeholder="your.email@gmail.com"
                autoComplete="email"
                value={checkoutForm.email}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-[#1D3557] mb-1 block">Phone Number</label>
              <Input
                data-testid="checkout-phone"
                type="tel"
                placeholder="+91 9XXXXXXXXX"
                autoComplete="tel"
                value={checkoutForm.phone}
                onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              {!showReferralInput ? (
                <button
                  type="button"
                  data-testid="show-referral-link"
                  onClick={() => setShowReferralInput(true)}
                  className="text-xs text-[#3D5A80] hover:text-[#1D3557] underline underline-offset-2"
                >
                  Have a referral code?
                </button>
              ) : (
                <div>
                  <label className="text-sm font-bold text-[#1D3557] mb-1 block">Referral Code</label>
                  <Input
                    data-testid="checkout-referral-code"
                    placeholder="Enter referral code"
                    autoFocus
                    value={checkoutForm.referral_code}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, referral_code: e.target.value }))}
                  />
                </div>
              )}
            </div>

            <Button
              data-testid="proceed-to-pay-btn"
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full py-5 text-lg font-bold bg-[#1D3557] hover:bg-[#2D4A6F] text-white rounded-xl"
            >
              {isProcessing ? 'Processing...' : `Pay ₹${totalPrice.toLocaleString('en-IN')}`}
            </Button>
            <div className="text-center space-y-1">
              <p className="text-xs text-gray-500">One-time payment · No auto-renewal · Secure UPI and card payment</p>
              <a
                href="https://wa.me/919328705924"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="checkout-whatsapp-link"
                className="text-xs text-[#06D6A0] hover:underline font-medium"
              >
                Questions? WhatsApp us
              </a>
              <p className="text-xs text-gray-400">
                Plans are non-refundable.{' '}
                <a href="/refunds" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                  See refund policy
                </a>
              </p>
            </div>
            <p className="text-xs text-center text-gray-500">
              After payment you&apos;ll set your password and your account is created instantly — no extra steps.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <SchoolEnquiryDialog open={showSchoolEnquiry} onOpenChange={setShowSchoolEnquiry} />
    </section>
  );
}
